import { useEffect, useState } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { withSavedIds } from '../product-body.ts';
import { productFormIssues, type ProductFormValues } from '../product-form.ts';
import type { Product } from '../product.ts';

export const AUTOSAVE_DELAY_MS = 3000;

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'failed';

type AutosaveInput = {
  readonly form: UseFormReturn<ProductFormValues>;
  readonly enabled: boolean;
  readonly busy: boolean;
  readonly persist: (values: ProductFormValues) => Promise<Product>;
};

export const useDraftAutosave = ({ form, enabled, busy, persist }: AutosaveInput): AutosaveState => {
  const [state, setState] = useState<AutosaveState>('idle');
  const values = useWatch({ control: form.control });
  const { isDirty } = form.formState;

  useEffect(() => {
    if (!enabled || busy || !isDirty) return undefined;
    const timer = setTimeout(() => {
      const snapshot = form.getValues();
      if (productFormIssues(snapshot).length > 0) return;
      setState('saving');
      persist(snapshot).then(
        (saved) => {
          form.setValue('variants', withSavedIds(form.getValues(), saved).variants);
          form.reset(withSavedIds(snapshot, saved), { keepValues: true });
          setState('saved');
        },
        () => {
          setState('failed');
        },
      );
    }, AUTOSAVE_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [values, enabled, busy, isDirty, form, persist]);

  return state;
};
